import * as React from "react";
import { Form, Label } from "semantic-ui-react";
import { createValidator } from "not-valid";
import * as moment from "moment";

import { FormBaseComponent, SaveDispatchProps, FormState } from "@neworbit/simpleui-forms";
import { Input } from "@neworbit/simpleui-input";

import { OrgStructureDetailModel } from "../../../crud/orgStructure";

import {
    ClinicCreateModel,
    ClinicType,
    ClinicLocationType,
    ClinicErrors,
    ALL_CUSTOMERS,
    ALL_CUSTOMERS_OPTION,
    ALL_CUSTOMERS_VALUE,
    OptionValue,
    BillableByTypeOptions,
    shouldShowBillableCustomerField
} from "../models";
import { RoomSelector } from "../../components/RoomSelector";
import { ClinicianSelector } from "../../components/ClinicianSelector";
import { PartialCustomerListModel } from "../../../crud/customer";

import { BillableOrgStructure1Dropdown } from "./BillableOrgStructure1Dropdown";

interface Props {
    clinicTypes: ClinicType[];
    clinicLocationTypes: ClinicLocationType[];
    createClinicErrors: ClinicErrors;
    customers: PartialCustomerListModel[];
}

interface OwnState {
    showConflictErrors?: boolean;
}

export class CreateForm extends FormBaseComponent<ClinicCreateModel, Props, FormState<ClinicCreateModel> & OwnState> {
    private notInPastValidator = createValidator((value: moment.Moment) => value && value.isSameOrAfter(moment()), "Must not be in the past");
    private notZeroValidator = createValidator((value: number) => value > 0, "Must not be zero");
    private isIncludedInBookableCustomers = createValidator((value: string) => {
        const customerOptions = this.getBookableCustomerOptions();
        const selectedBillableCustomerId = customerOptions.find(o => o.value === value)?.value || "";
        const bookableCustomerIds = this.state.values.customers.map(c => c.id);
        return bookableCustomerIds.includes(ALL_CUSTOMERS_VALUE.id) || bookableCustomerIds.includes(selectedBillableCustomerId);
    }, "Billable Customer must be included in the Bookable Customers list");

    constructor(props: Props & OwnState & SaveDispatchProps<ClinicCreateModel>) {
        super(props);
        this.state = {
            values: {
                start: null,
                end: null,
                cliniciansRequired: 1,
                clinicType: null,
                clinicLocationTypes: [],
                room: null,
                slotLengthInMinutes: undefined,
                teamPreventUsers: [],
                customers: [ALL_CUSTOMERS_VALUE],
                billableByType: null,
                billableCustomers: [],
                billableOrgStructure: undefined
            },
            valid: {}
        };
        this.notInPastValidator = this.notInPastValidator.bind(this.notInPastValidator);
        this.notZeroValidator = this.notZeroValidator.bind(this.notZeroValidator);
        this.isIncludedInBookableCustomers = this.isIncludedInBookableCustomers.bind(this.isIncludedInBookableCustomers);
    }

    public render() {
        const { start, end } = this.state.values;
        const calendarHighlight = start && end && start.isValid() && end.isValid() && { start, end };
        const billableCustomerValues = (this.state.values.billableCustomers && this.state.values.billableCustomers.map(l => l.id)) ?? [];
        const billableCustomerValue = (billableCustomerValues.length > 0 && billableCustomerValues[0]) || "";
        const bookableCustomerValues = (this.state.values.customers && this.state.values.customers.map(l => l.id)) ?? [];

        return (
            <Form onSubmit={this.handleSubmit} className="clinic-create-form">
                <Input.Dropdown
                    dynamicOptions
                    label="Product Type"
                    value={this.state.values.clinicType?.name}
                    showErrors={this.state.showErrors}
                    required
                    options={this.props.clinicTypes.map(clinicType =>
                        ({ key: clinicType.name, value: clinicType.name, text: clinicType.name }))}
                    onChange={this.onProductTypeChange}
                />
                <Input.DropdownMulti
                    dynamicOptions
                    label="Clinic Types"
                    value={this.state.values.clinicLocationTypes && this.state.values.clinicLocationTypes.map(l => l.name)}
                    showErrors={this.state.showErrors}
                    required
                    options={this.props.clinicLocationTypes.map(clinicLocationType =>
                        ({ key: clinicLocationType.name, value: clinicLocationType.name, text: clinicLocationType.name }))}
                    onChange={(value, valid) => {
                        const selectedLocationTypes = this.props.clinicLocationTypes.filter(l => value.indexOf(l.name) !== -1);
                        this.updateProperty("clinicLocationTypes", selectedLocationTypes, valid);
                        const selectedRoom = this.state.values.room;
                        this.updateProperty("room", selectedRoom, !(selectedLocationTypes.find(l => l.requiresRoom) && !selectedRoom));
                    }}
                />
                <Input.Dropdown
                    dynamicOptions
                    label="Billable By"
                    value={this.state.values.billableByType?.name}
                    showErrors={this.state.showErrors}
                    required
                    options={BillableByTypeOptions.map(billableByType =>
                        ({ key: billableByType.name, value: billableByType.name, text: billableByType.name }))}
                    onChange={this.onBillableByTypeChange}
                />
                {
                    <>
                        {shouldShowBillableCustomerField(this.state.values.billableByType) &&
                            <Input.Dropdown
                                dynamicOptions
                                label="Billable Customer"
                                value={billableCustomerValue}
                                showErrors={this.state.showErrors}
                                validation={[this.isIncludedInBookableCustomers]}
                                required
                                options={this.getBillableCustomerOptions()}
                                onChange={this.onBillableCustomerChange}
                                search
                            />
                        }
                        {billableCustomerValue && (<BillableOrgStructure1Dropdown
                            billableCustomerId={billableCustomerValue}
                            value={this.state.values.billableOrgStructure?.id}
                            onChange={this.onBillableOrgStructureChange}
                            showErrors={this.state.showErrors}
                        />)}
                    </>
                }
                <Input.Datetime
                    label="Start"
                    value={this.state.values.start}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={(value, valid) => {
                        const newValue = value?.utc(true);
                        const datesValid = this.validateStartBeforeEndDate(newValue, this.state.values.end) &&
                            this.validateStartEndDateSameDay(newValue, this.state.values.end);
                        this.updateProperty("start", newValue, valid && datesValid);
                        this.updateProperty("end", this.state.values.end, this.notInPastValidator(this.state.values.end).type === "pass" && datesValid);
                        this.setState({ showConflictErrors: false });
                    }}
                />
                <Input.Datetime
                    label="End"
                    value={this.state.values.end}
                    showErrors={this.state.showErrors}
                    validation={[this.notInPastValidator]}
                    required
                    onChange={(value, valid) => {
                        const newValue = value?.utc(true);
                        const datesValid = this.validateStartBeforeEndDate(this.state.values.start, newValue) &&
                            this.validateStartEndDateSameDay(this.state.values.start, newValue);
                        this.updateProperty("end", newValue, valid);
                        this.updateProperty("start", this.state.values.start, this.notInPastValidator(this.state.values.start).type === "pass" && datesValid);
                        this.setState({ showConflictErrors: false });
                    }}
                />
                {this.state.showErrors && !this.validateStartBeforeEndDate(this.state.values.start, this.state.values.end) &&
                    <Label basic color="red" pointing content="The start time must be before the end" />}
                {this.state.showErrors && !this.validateStartEndDateSameDay(this.state.values.start, this.state.values.end) &&
                    <Label basic color="red" pointing content="The start time must be the same day as the end" />}
                <RoomSelector
                    selectedRoomName={this.state.values.room?.name}
                    highlight={calendarHighlight}
                    onRoomSelected={(id, name) => {
                        this.updateProperty("room", { id, name }, true);
                        this.setState({ showConflictErrors: false });
                    }}
                    onRoomDeselected={() => {
                        this.updateProperty("room", null, false);
                        this.setState({ showConflictErrors: false });
                    }}
                />
                {this.state.showErrors && this.state.values.clinicLocationTypes.find(l => l.requiresRoom) && !this.state.values.room &&
                    <Label basic color="red" pointing content="A room must be selected" />}
                {this.state.showConflictErrors && this.props.createClinicErrors && this.props.createClinicErrors.errors.roomId &&
                    <Label basic color="red" pointing content={this.props.createClinicErrors.errors.roomId} />}
                <Input.Number
                    label="Clinicians Required"
                    value={this.state.values.cliniciansRequired}
                    showErrors={this.state.showErrors}
                    required
                    // eslint-disable-next-line react/jsx-no-bind
                    onChange={(value, valid) => this.updateProperty("cliniciansRequired", value, valid)}
                />
                <ClinicianSelector
                    restrictToOneClinician
                    selectedClinicians={this.state.values.teamPreventUsers}
                    highlight={calendarHighlight}
                    conflicts={this.state.showConflictErrors && this.props.createClinicErrors && this.props.createClinicErrors.errors.userClashIds}
                    onCliniciansChanged={clinicians => {
                        this.updateProperty("teamPreventUsers", clinicians, clinicians.length <= 1);
                        this.setState({ showConflictErrors: false });
                    }}
                />
                {this.state.values.clinicType && this.state.values.clinicType.usesSlots &&
                    <Input.Number
                        label="Appointment Length (minutes)"
                        value={this.state.values.slotLengthInMinutes}
                        validation={[this.notZeroValidator]}
                        showErrors={this.state.showErrors}
                        required
                        onChange={(value, valid) => {
                            this.updateProperty("slotLengthInMinutes", value, value > 0 && valid);
                            this.setState({ showConflictErrors: false });
                        }}
                    />
                }
                <Input.DropdownMulti
                    dynamicOptions
                    label="Bookable Customers"
                    value={bookableCustomerValues}
                    showErrors={this.state.showErrors}
                    required
                    options={this.getBookableCustomerOptions()}
                    onChange={this.onBookableCustomerChange}
                    search
                />
            </Form>
        );
    }

    public submit = async () => {
        await this.handleSubmit({ preventDefault: (): void => undefined } as any);
        this.setState({ showConflictErrors: true });
    };

    private validateStartBeforeEndDate(start?: moment.Moment, end?: moment.Moment) {
        return !start || !end || end.diff(start) > 0;
    }

    private validateStartEndDateSameDay(start?: moment.Moment, end?: moment.Moment) {
        return !start || !end || end.isSame(start, "day");
    }

    private getBillableCustomerOptions = () => {
        return this.props.customers.map(customer => ({ key: customer.id, value: customer.id, text: customer.name }));
    };

    private onProductTypeChange = (value: string, valid: boolean) => {
        const clinic = this.props.clinicTypes.find(clinicType => clinicType.name === value);

        this.updateProperty("clinicType", clinic , valid);

        if (clinic && !clinic.usesSlots)
        {
            this.updateProperty("slotLengthInMinutes", undefined, true);
        }
    };

    private onBillableByTypeChange = (value: string, valid: boolean) => {
        if (!value) {
            this.updateProperty("billableByType", null, valid);
        }
        else {
            this.updateProperty("billableByType", BillableByTypeOptions.find(billableByType => billableByType.name === value), valid);
        }

        this.updateProperty("billableOrgStructure", undefined, true);
        this.updateProperty("billableCustomers", [], true);
    };

    private onBillableCustomerChange = (value: string, valid: boolean) => {
        if (value) {
            const customerOptions = this.getBillableCustomerOptions();
            const billableCustomerOption = customerOptions.find(o => o.value === value);
            const billableCustomer = { id: billableCustomerOption?.value, name: billableCustomerOption?.text };

            this.updateProperty("billableCustomers", [billableCustomer], valid);
        }
        else {
            this.updateProperty("billableCustomers", [], valid);
        }
    };

    private onBillableOrgStructureChange = (orgStructure: OrgStructureDetailModel | undefined, valid: boolean) => {
        if (orgStructure) {
            this.updateProperty("billableOrgStructure", { id: orgStructure.id, name: orgStructure.name }, valid);
        }
        else {
            this.updateProperty("billableOrgStructure", undefined, valid);
        }
    };

    private getBookableCustomerOptions = () => {
        return [
            ALL_CUSTOMERS_OPTION,
            ...this.props.customers.map(customer => ({ key: customer.id, value: customer.id, text: customer.name }))
        ];
    };

    private onBookableCustomerChange = (value: any[], valid: boolean) => {
        if (value?.length === 0) {
            this.updateProperty("customers", [], valid);
        } else {
            const customerOptions = this.getBookableCustomerOptions();
            const selectedCustomers = customerOptions.filter(o => value.indexOf(o.value) !== -1) ?? [];
            const isOnlyAllOptionSelected = selectedCustomers.findIndex(customer => customer.value === ALL_CUSTOMERS) !== -1 && selectedCustomers.length === 1;
            if (isOnlyAllOptionSelected) {
                this.updateProperty("customers", [ALL_CUSTOMERS_VALUE], valid);
            } else {
                const newCustomers = selectedCustomers.filter(sc => sc.value !== ALL_CUSTOMERS).map<OptionValue>(sc => ({ id: sc.value, name: sc.text || "" }));
                this.updateProperty("customers", newCustomers, valid);
            }
        }
    };
}
